import { useState } from 'react';
import { postApi } from 'views/services/api';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Typography,
  Stack,
  Alert,
  Collapse,
  CircularProgress,
  LinearProgress,
  Fade
} from '@mui/material';

// third party
import * as Yup from 'yup';
import { Formik } from 'formik';

// project imports
import AnimateButton from 'ui-component/extended/AnimateButton';

// icons
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';

// ============================|| FORGOT PASSWORD FORM ||============================ //

const AuthForgotPassword = ({ ...others }) => {
  const theme = useTheme();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [sentEmail, setSentEmail] = useState('');
  const [errorStatus, setErrorStatus] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  if (emailSent) {
    return (
      <Fade in={true} timeout={600}>
        <Box sx={{ textAlign: 'center', py: 2 }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: theme.palette.success.light + '20',
              borderRadius: '50%',
              p: 3,
              mb: 3,
              border: '2px solid',
              borderColor: theme.palette.success.light + '50'
            }}
          >
            <MarkEmailReadOutlinedIcon
              sx={{ fontSize: 48, color: theme.palette.success.main }}
            />
          </Box>

          <Typography variant="h4" sx={{ fontWeight: 600, mb: 1, color: theme.palette.grey[800] }}>
            ¡Correo Enviado!
          </Typography>

          <Typography variant="body2" sx={{ color: theme.palette.grey[600], mb: 2, lineHeight: 1.6 }}>
            Hemos enviado un enlace de recuperación a
          </Typography>

          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              color: theme.palette.primary.main,
              backgroundColor: theme.palette.primary.light + '15',
              borderRadius: '8px',
              py: 1,
              px: 2,
              display: 'inline-block',
              mb: 3
            }}
          >
            {sentEmail}
          </Typography>

          <Typography variant="body2" sx={{ color: theme.palette.grey[500], fontSize: '0.8rem', lineHeight: 1.6 }}>
            Revisa tu bandeja de entrada y la carpeta de spam. El enlace es válido por 1 hora.
          </Typography>
        </Box>
      </Fade>
    );
  }

  return (
    <>
      <Collapse in={errorStatus}>
        <Alert
          severity="error"
          icon={<ErrorOutlineIcon fontSize="inherit" />}
          onClose={() => setErrorStatus(false)}
          sx={{
            mb: 3,
            borderRadius: '8px',
            backgroundColor: theme.palette.error.light + '20',
            color: theme.palette.error.dark,
            border: '1px solid',
            borderColor: theme.palette.error.light + '50'
          }}
        >
          {errorMessage}
        </Alert>
      </Collapse>

      <Typography variant="body2" sx={{ color: theme.palette.grey[600], mb: 3, lineHeight: 1.6 }}>
        Ingresa el correo electrónico asociado a tu cuenta y te enviaremos un enlace para restablecer tu contraseña.
      </Typography>

      <Formik
        initialValues={{ email: '' }}
        validationSchema={Yup.object().shape({
          email: Yup.string()
            .email('Debe ser un correo electrónico válido')
            .max(255)
            .required('El correo electrónico es requerido')
        })}
        onSubmit={async (values) => {
          setIsSubmitting(true);
          setErrorStatus(false);
          try {
            const response = await postApi('api/hotel/forgot-password', { email: values.email });
            if (response.status === 200) {
              setSentEmail(values.email);
              setEmailSent(true);
            } else {
              setErrorStatus(true);
              setErrorMessage(response.response?.data?.message || 'Error al enviar el correo');
            }
          } catch (err) {
            setErrorStatus(true);
            setErrorMessage('Error de conexión con el servidor');
          } finally {
            setIsSubmitting(false);
          }
        }}
      >
        {({ errors, handleBlur, handleChange, handleSubmit, touched, values }) => (
          <form noValidate onSubmit={handleSubmit} {...others}>
            <Stack spacing={3}>
              <FormControl fullWidth error={Boolean(touched.email && errors.email)}>
                <InputLabel htmlFor="forgot-password-email">Correo Electrónico</InputLabel>
                <OutlinedInput
                  id="forgot-password-email"
                  type="email"
                  value={values.email}
                  name="email"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  startAdornment={
                    <InputAdornment position="start">
                      <EmailOutlinedIcon sx={{ color: theme.palette.grey[500] }} />
                    </InputAdornment>
                  }
                  label="Correo Electrónico"
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.grey[300]
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.primary.light
                    }
                  }}
                />
                {touched.email && errors.email && (
                  <FormHelperText error>{errors.email}</FormHelperText>
                )}
              </FormControl>

              {isSubmitting && (
                <Box sx={{ width: '100%' }}>
                  <LinearProgress sx={{ height: 2, borderRadius: 1 }} />
                </Box>
              )}

              <Box>
                <AnimateButton>
                  <Button
                    disableElevation
                    fullWidth
                    size="large"
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={isSubmitting}
                    sx={{
                      borderRadius: '8px',
                      textTransform: 'none',
                      fontSize: '1rem',
                      fontWeight: 600,
                      padding: '12px 0',
                      boxShadow: 'none',
                      '&:hover': {
                        backgroundColor: theme.palette.primary.dark,
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                      }
                    }}
                  >
                    {isSubmitting ? (
                      <Stack direction="row" spacing={1} alignItems="center">
                        <CircularProgress size={20} color="inherit" />
                        <span>Enviando...</span>
                      </Stack>
                    ) : (
                      'Enviar Enlace de Recuperación'
                    )}
                  </Button>
                </AnimateButton>
              </Box>
            </Stack>
          </form>
        )}
      </Formik>
    </>
  );
};

export default AuthForgotPassword;
